import { useState } from 'react';
import { Modal } from '@shared/Modal';
import Button from '@shared/Button';
import { AlertTriangle, Info } from 'lucide-react';

interface Document {
  originalName: string;
  queryCount?: number;
  [key: string]: any;
}

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (deletePhysicalFile: boolean) => Promise<void>;
  document: Document | null;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({ isOpen, onClose, onConfirm, document }) => {
  const [acknowledgement, setAcknowledgement] = useState(false);
  const [deletePhysicalFile, setDeletePhysicalFile] = useState(false); // New state for physical deletion
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    await onConfirm(deletePhysicalFile); // Pass the new state to onConfirm
    setLoading(false);
    setAcknowledgement(false); // Reset for next time
    setDeletePhysicalFile(false); // Reset for next time
  };

  if (!document) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Document?">
      <div className="text-center p-4">
        <AlertTriangle className="w-16 h-16 text-error mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-900 mb-3">Are you sure you want to delete '{document.originalName}'?</h3>
        <p className="text-gray-600 mb-4">
          This action cannot be undone. Once deleted, the document will be permanently removed from your knowledge base.
        </p>

        {/* Placeholder for impact warning - actual data would come from API */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 text-sm text-yellow-800 flex items-start space-x-2 rounded-md mb-4">
          <Info className="w-5 h-5 text-yellow-600 flex-shrink-0" />
          <p className="flex-grow">This document is referenced by <span className="font-semibold">{document.queryCount || 0} queries</span>. Deleting it may impact past query responses.</p>
        </div>

        {/* New checkbox for physical deletion */}
        <div className="flex items-center mt-4">
          <input
            id="deletePhysicalFile"
            name="deletePhysicalFile"
            type="checkbox"
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            checked={deletePhysicalFile}
            onChange={(e) => setDeletePhysicalFile(e.target.checked)}
          />
          <label htmlFor="deletePhysicalFile" className="ml-2 block text-sm text-gray-900">
            Also delete the physical file from storage.
          </label>
        </div>

        <div className="flex items-center mt-4">
          <input
            id="acknowledgement"
            name="acknowledgement"
            type="checkbox"
            className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
            checked={acknowledgement}
            onChange={(e) => setAcknowledgement(e.target.checked)}
          />
          <label htmlFor="acknowledgement" className="ml-2 block text-sm text-gray-900">
            I understand this will permanently delete the document record.
          </label>
        </div>
      </div>

      <div className="flex justify-end space-x-2 mt-6">
        <Button onClick={onClose} variant="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          variant="danger"
          disabled={!acknowledgement || loading}
          loading={loading}
        >
          Delete Document
        </Button>
      </div>
    </Modal>
  );
};

export default DeleteConfirmModal;
